import * as THREE from 'three';
import { CSS3DObject } from 'three/examples/jsm/renderers/CSS3DRenderer.js';
import type {
  ClaimElement,
  ConceptElement,
  DomainElement,
  FeatureElement,
  PaperElement,
  PhenomenonElement,
  RegionLabel,
  SystemElement,
} from '../data/types';

const PANEL_SCALE = 0.013;

export interface PanelHandle {
  id: string;
  element: SystemElement;
  cssObj: CSS3DObject;
  domElement: HTMLElement;
}

function makePanelEl(): HTMLDivElement {
  const el = document.createElement('div');
  el.className = 'panel';
  return el;
}

function categoryClass(el: SystemElement): string {
  switch (el.kind) {
    case 'concept': return `cat-${(el as ConceptElement).group}`;
    case 'claim': return 'cat-claim';
    case 'phenomenon': return 'cat-phenomenon';
    case 'paper': return `cat-paper status-${(el as PaperElement).status}`;
    case 'domain': return 'cat-domain';
    case 'feature': return 'cat-feature';
  }
}

function renderConcept(c: ConceptElement, el: HTMLDivElement) {
  el.innerHTML = `
    <div class="eyebrow">
      <span>${c.group === 'foundational' ? 'Foundational' :
              c.group === 'formal' ? 'Formal Apparatus' :
              c.group === 'diagnostic' ? 'Diagnostic' : 'Derived'} Concept</span>
      <span class="num">${c.index}</span>
    </div>
    <h3 class="title">${c.title}</h3>
    <p class="blurb">${c.blurb}</p>
  `;
}

function renderClaim(c: ClaimElement, el: HTMLDivElement) {
  el.innerHTML = `
    <div class="eyebrow">
      <span>${c.status === 'foundational' ? 'Foundational Claim' : 'Derived Claim'}</span>
      <span class="num">${c.index}</span>
    </div>
    <h3 class="title">"${c.title}"</h3>
    <p class="blurb">${c.blurb}</p>
  `;
}

function renderPhenomenon(p: PhenomenonElement, el: HTMLDivElement) {
  const domainLabel: Record<PhenomenonElement['domain'], string> = {
    foundational: 'Metaphysical',
    qm: 'Quantum',
    modal: 'Modal',
    agency: 'Agency',
    temporal: 'Temporal',
  };
  el.innerHTML = `
    <div class="eyebrow">
      <span>${domainLabel[p.domain]} Phenomenon</span>
      <span class="num">${p.index}</span>
    </div>
    <h3 class="title">${p.title}</h3>
    <p class="blurb">${p.blurb}</p>
  `;
}

function renderPaper(p: PaperElement, el: HTMLDivElement) {
  const statusLabel: Record<PaperElement['status'], string> = {
    published: 'Published',
    review: 'Under Review',
    progress: 'In Progress',
    planned: 'Planned',
  };
  el.innerHTML = `
    <div class="eyebrow">
      <span>${statusLabel[p.status]}</span>
      <span class="num">${p.index}</span>
    </div>
    <h3 class="title">${p.title}</h3>
    <p class="blurb">${p.subtitle ?? p.blurb}</p>
    <div class="rule"></div>
    <div class="meta"><span>${p.venue ?? '—'}</span><span>${p.authors ?? ''}</span></div>
  `;
}

function renderDomain(d: DomainElement, el: HTMLDivElement) {
  el.innerHTML = `
    <div class="eyebrow">
      <span>Domain</span>
      <span class="num">${d.index}</span>
    </div>
    <h3 class="title">${d.title}</h3>
    <p class="blurb">${d.blurb}</p>
    <ul class="sub-list">
      ${d.positions.map((p) => `<li>${p}</li>`).join('')}
    </ul>
  `;
}

function renderFeature(f: FeatureElement, el: HTMLDivElement) {
  el.innerHTML = `
    <div class="ornament">❦</div>
    <div class="eyebrow"><span>The Unifying Feature</span></div>
    <h3 class="title">${f.title}</h3>
    <p class="blurb">${f.blurb}</p>
  `;
}

export function createPanel(el: SystemElement): PanelHandle {
  const dom = makePanelEl();
  dom.classList.add(...categoryClass(el).split(' '));
  dom.dataset.id = el.id;
  dom.dataset.kind = el.kind;
  // Stop pointerdown bubbling to OrbitControls — three's controller uses
  // setPointerCapture on its dom element, which would redirect mouseup away
  // from the panel and kill the click event we depend on.
  dom.addEventListener('pointerdown', (e) => e.stopPropagation());

  switch (el.kind) {
    case 'concept': renderConcept(el as ConceptElement, dom); break;
    case 'claim': renderClaim(el as ClaimElement, dom); break;
    case 'phenomenon': renderPhenomenon(el as PhenomenonElement, dom); break;
    case 'paper': renderPaper(el as PaperElement, dom); break;
    case 'domain': renderDomain(el as DomainElement, dom); break;
    case 'feature': renderFeature(el as FeatureElement, dom); break;
  }

  const obj = new CSS3DObject(dom);
  obj.position.set(el.position[0], el.position[1], el.position[2]);
  obj.scale.set(PANEL_SCALE, PANEL_SCALE, PANEL_SCALE);

  // Subtle rotation for non-feature panels — gentle tilt by region.
  // Feature stays flat-on. Otherwise rotate softly toward origin so the
  // composition has gentle facing rather than a uniform billboard look.
  if (el.kind !== 'feature') {
    const dx = -el.position[0];
    const dz = -el.position[2];
    const yaw = Math.atan2(dx, Math.abs(dz) + 6) * 0.25;
    obj.rotation.y = yaw;
    // gentle pitch from height
    obj.rotation.x = -el.position[1] * 0.012;
  }

  return { id: el.id, element: el, cssObj: obj, domElement: dom };
}

export function createRegionLabel(label: RegionLabel): CSS3DObject {
  const dom = document.createElement('div');
  dom.className = 'region-label';
  dom.innerHTML = label.roman
    ? `<span class="roman">${label.roman}.</span>${label.text}`
    : label.text;
  const obj = new CSS3DObject(dom);
  obj.position.set(label.position[0], label.position[1], label.position[2]);
  obj.scale.set(PANEL_SCALE, PANEL_SCALE, PANEL_SCALE);
  return obj;
}

export const _PANEL_SCALE = PANEL_SCALE;

// Keep tree-shaking happy with THREE import on panels file
void THREE;
